'use client';

import { MyQuestNavigationMenu, QuestNavigationMenu } from '@diritto/common/navigation/constant';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { MyQuestNavigationBar } from 'app-components/common/navigations/MyQuestNavigationBar';
import { QuestNavigationBar } from 'app-components/common/navigations/QuestNavigationBar';
import { MyRankInfo } from 'app-components/quest/MyRankInfo';
import { QuestList } from 'app-components/quest/QuestList';
import { useAuthState } from 'contexts/AuthProvider';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useGetActivityPointMyRankQuery } from 'queries/activityPoint/useGetActivityPointMyRankQuery';
import { useGetActivityPointRankExcludedQuery } from 'queries/activityPoint/useGetActivityPointRankExcludedQuery';
import { usePostRelayProfilesLiteQuery } from 'queries/relay/usePostRelayProfilesLiteQuery';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useActivityPointSeason } from 'state-hooks/activityPoint/useActivityPointSeason';
import { BlockAuth } from 'ui-components/common/BlockAuth';
import { Divider } from 'ui-components/common/Divider';
import { EmptyList } from 'ui-components/common/EmptyList';
import { Loading } from 'ui-components/common/Loading';
import { ContentBodyContainer } from 'ui-components/layout/containers/ContentBodyCenterContainer';
import { StyledTopMenu } from 'ui-components/navigations/styles';

export const OngoingSeasonPage = () => {
  const { isLoggedIn, isLoaded: isAuthLoaded, profile } = useAuthState();
  const { tracker } = useTrackingState();

  useEffect(() => {
    tracker.track(TrackingEventType.ViewQuestOngoingSeason);
  }, [tracker]);

  const { currentSeason, isPending: isCurrentSeasonPending } = useActivityPointSeason();
  const { data: activityPointMyRankData, isPending: isActivityPointMyRankDataPending } =
    useGetActivityPointMyRankQuery(isLoggedIn);
  const { data: rankExcludedData, isPending: isRankingExcludedPending } =
    useGetActivityPointRankExcludedQuery(isLoggedIn);

  const myRank = useMemo(
    () => profile && activityPointMyRankData?.rank.find(rank => rank.profileId === profile.id),
    [activityPointMyRankData, profile],
  );

  const profileIds = useMemo(
    () => [...(activityPointMyRankData?.rank.map(rank => rank.profileId) ?? [])],
    [activityPointMyRankData?.rank],
  );
  const { isPending: isProfileRelayDataPending } = usePostRelayProfilesLiteQuery({
    profileIds,
  });

  const isLoadingLoggedInData = isLoggedIn && (isActivityPointMyRankDataPending || isRankingExcludedPending);

  const isLoading =
    !isAuthLoaded ||
    isCurrentSeasonPending ||
    (profileIds.length > 0 && isProfileRelayDataPending) ||
    isLoadingLoggedInData;

  const renderBody = useCallback(() => {
    if (isLoading) {
      return <Loading className="flex h-[50vh]" />;
    }

    if (!currentSeason) {
      return <EmptyList title="시즌이 시작되면 공개돼요." description="조금만 기다려 주세요." />;
    }

    if (currentSeason && myRank) {
      return (
        <>
          <MyRankInfo
            season={currentSeason}
            myRank={myRank}
            activityPointRankExcluded={rankExcludedData?.activityPointRankExcluded}
          />
          <QuestList />
          <ContentBodyContainer>
            <ul className="list-disc ml-4 text-gray-500 font-medium text-xxs">
              <li className="mt-1">부정한 방법으로 퀘스트를 수행하는 경우 이용이 제재돼요.</li>
            </ul>
          </ContentBodyContainer>
        </>
      );
    }

    return <EmptyList title="시즌이 시작되면 공개돼요." description="조금만 기다려 주세요." />;
  }, [currentSeason, isLoading, myRank, rankExcludedData?.activityPointRankExcluded]);

  return (
    <>
      <StyledTopMenu>
        <QuestNavigationBar selectedMenu={QuestNavigationMenu.MyQuest} />
        <Divider />
      </StyledTopMenu>

      {isLoggedIn ? (
        <>
          <StyledTopMenu>
            <MyQuestNavigationBar selectedMenu={MyQuestNavigationMenu.OngoingSeason} />
            <Divider />
          </StyledTopMenu>
          {renderBody()}
        </>
      ) : (
        <BlockAuth message="로그인 후 내 프로필 랭킹을 살펴보세요." />
      )}
    </>
  );
};
